import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import http from "../../services/http";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const statusBadge = (s = "") => {
  const map = { approved: ["#dcfce7","#16a34a"], rejected: ["#fee2e2","#dc2626"], pending: ["#fef9c3","#92400e"] };
  const [bg, color] = map[s.toLowerCase()] || ["#f1f5f9","#64748b"];
  return <span style={{ ...ts.badge, background: bg, color }}>{s || "—"}</span>;
};

const InfoRow = ({ label, value, mono }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
    <span style={{ fontSize: "0.73rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", flexShrink: 0, marginRight: 10 }}>{label}</span>
    <span style={{ fontSize: "0.84rem", color: "#1e293b", fontWeight: 600, textAlign: "right", ...(mono ? { fontFamily: "monospace", letterSpacing: "0.03em" } : {}) }}>
      {value || <span style={{ color: "#cbd5e1" }}>Not provided</span>}
    </span>
  </div>
);

export const UserPaymentDetailsModal = ({ show, onHide, user }) => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab,     setTab]     = useState("upi");

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await http.get("user_payment_details", { params: { user_id: user.id } });
      if (res.data?.status) setData(res.data);
      else toast.error(res.data?.msg || "Failed to load payment details");
    } catch { toast.error("Server error"); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { if (show) { setTab("upi"); load(); } }, [show, load]);

  const pd = data?.payment_details || {};
  const wr = data?.withdraw_requests || [];

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="lg">
      <div style={ms.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={ms.iconWrap}>
            <Icon icon="solar:card-bold-duotone" style={{ fontSize: 20 }} />
          </div>
          <div>
            <div style={ms.title}>Payment Details</div>
            <div style={ms.sub}>{user?.name} · {user?.mobile}</div>
          </div>
        </div>
        <button style={ms.closeBtn} onClick={onHide}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 16 }} />
        </button>
      </div>

      <div style={ms.body}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
            <span className="spinner-border spinner-border-sm" />
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div style={ms.tabRow}>
              {[
                { key: "upi",  label: "UPI Details",  icon: "solar:smartphone-bold-duotone" },
                { key: "bank", label: "Bank Details",  icon: "solar:bank-bold-duotone" },
                { key: "hist", label: "Withdrawal History", icon: "solar:transfer-horizontal-bold-duotone" },
              ].map((t) => (
                <button key={t.key} style={{ ...ms.tabBtn, ...(tab === t.key ? ms.tabActive : {}) }} onClick={() => setTab(t.key)}>
                  <Icon icon={t.icon} style={{ fontSize: 13, marginRight: 5 }} />
                  {t.label}
                  {t.key === "hist" && (
                    <span style={{ ...ms.tabCount, background: tab === t.key ? "rgba(255,255,255,0.25)" : "#f1f5f9" }}>
                      {wr.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* UPI */}
            {tab === "upi" && (
              <div style={ms.detailCard}>
                <div style={ms.detailCardHeader}>
                  <Icon icon="solar:smartphone-bold-duotone" style={{ fontSize: 16, color: "#16632b", marginRight: 6 }} />
                  UPI Payment Info
                </div>
                {pd.preferred_method && (
                  <div style={{ marginBottom: 10 }}>
                    <span style={ms.prefBadge}>
                      {pd.preferred_method === "upi" ? "Preferred: UPI" : `Preferred: ${pd.preferred_method}`}
                    </span>
                  </div>
                )}
                <InfoRow label="UPI ID" value={pd.upi_id} mono />
                {!pd.upi_id && (
                  <div style={ms.emptyMsg}>
                    <Icon icon="solar:info-circle-bold-duotone" style={{ fontSize: 14, marginRight: 5, color: "#f59e0b" }} />
                    No UPI ID saved by this user
                  </div>
                )}
              </div>
            )}

            {/* Bank */}
            {tab === "bank" && (
              <div style={ms.detailCard}>
                <div style={ms.detailCardHeader}>
                  <Icon icon="solar:bank-bold-duotone" style={{ fontSize: 16, color: "#16632b", marginRight: 6 }} />
                  Bank Account Details
                </div>
                {pd.preferred_method === "bank" && (
                  <div style={{ marginBottom: 10 }}>
                    <span style={ms.prefBadge}>Preferred: Bank Transfer</span>
                  </div>
                )}
                <InfoRow label="Account Holder" value={pd.account_holder_name} />
                <InfoRow label="Bank Name"       value={pd.bank_name} />
                <InfoRow label="Account Number"  value={pd.account_number} mono />
                <InfoRow label="IFSC Code"       value={pd.ifsc_code} mono />
                {!pd.bank_name && !pd.account_number && (
                  <div style={ms.emptyMsg}>
                    <Icon icon="solar:info-circle-bold-duotone" style={{ fontSize: 14, marginRight: 5, color: "#f59e0b" }} />
                    No bank details saved by this user
                  </div>
                )}
              </div>
            )}

            {/* Withdrawal History */}
            {tab === "hist" && (
              wr.length === 0 ? (
                <div style={ms.emptyMsg}>
                  <Icon icon="solar:transfer-horizontal-bold-duotone" style={{ fontSize: 14, marginRight: 5, color: "#94a3b8" }} />
                  No withdrawal requests found
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={ms.table}>
                    <thead>
                      <tr>
                        <th style={ms.th}>#</th>
                        <th style={{ ...ms.th, textAlign: "right" }}>Amount</th>
                        <th style={ms.th}>UPI / Bank</th>
                        <th style={ms.th}>Account</th>
                        <th style={ms.th}>IFSC</th>
                        <th style={ms.th}>Status</th>
                        <th style={ms.th}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wr.map((r, idx) => (
                        <tr key={r.id || idx}
                          style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                          onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}
                        >
                          <td style={ms.td}><span style={{ fontSize: "0.72rem", color: "#94a3b8" }}>{idx + 1}</span></td>
                          <td style={{ ...ms.td, textAlign: "right" }}>
                            <span style={{ fontWeight: 700, color: "#dc2626" }}>{fmt(r.amount)}</span>
                          </td>
                          <td style={ms.td}><span style={{ fontFamily: "monospace", fontSize: "0.78rem" }}>{r.upi_id || "—"}</span></td>
                          <td style={ms.td}><span style={{ fontFamily: "monospace", fontSize: "0.78rem" }}>{r.account_number || "—"}</span></td>
                          <td style={ms.td}><span style={{ fontFamily: "monospace", fontSize: "0.78rem" }}>{r.ifsc_code || "—"}</span></td>
                          <td style={ms.td}>{statusBadge(r.status)}</td>
                          <td style={ms.td}><span style={{ fontSize: "0.75rem", color: "#64748b" }}>{r.created_at}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </>
        )}
      </div>

      <div style={ms.footer}>
        <button style={ms.closeFullBtn} onClick={onHide}>Close</button>
      </div>
    </Modal>
  );
};

const ms = {
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 22px 13px", borderBottom: "1px solid #f1f5f9" },
  iconWrap: { width: 38, height: 38, borderRadius: 9, background: "linear-gradient(135deg,#0369a1,#0ea5e9)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  title: { fontWeight: 800, fontSize: "0.98rem", color: "#0f172a" },
  sub:   { fontSize: "0.75rem", color: "#94a3b8" },
  closeBtn: { background: "#f1f5f9", border: "none", borderRadius: 7, width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" },
  body:   { padding: "16px 22px 20px" },
  footer: { display: "flex", justifyContent: "flex-end", padding: "10px 22px 14px", borderTop: "1px solid #f1f5f9" },

  tabRow: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  tabBtn: { display: "flex", alignItems: "center", padding: "7px 13px", border: "1.5px solid #e2e8f0", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: "0.79rem", fontWeight: 600, color: "#475569", fontFamily: "inherit" },
  tabActive: { background: "linear-gradient(135deg,#0369a1,#0ea5e9)", color: "#fff", border: "none" },
  tabCount: { marginLeft: 7, padding: "2px 6px", borderRadius: 100, fontSize: "0.67rem", fontWeight: 700 },

  detailCard: { background: "#f8fafc", borderRadius: 12, padding: "14px 18px", border: "1px solid #e8edf2" },
  detailCardHeader: { fontWeight: 700, fontSize: "0.85rem", color: "#0f172a", marginBottom: 12, display: "flex", alignItems: "center" },
  prefBadge: { display: "inline-flex", padding: "3px 10px", background: "#dcfce7", color: "#16a34a", borderRadius: 100, fontSize: "0.72rem", fontWeight: 700 },
  emptyMsg: { display: "flex", alignItems: "center", padding: "10px 0", color: "#94a3b8", fontSize: "0.8rem" },

  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.79rem" },
  th: { padding: "9px 12px", background: "#f8fafc", color: "#64748b", fontWeight: 700, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1.5px solid #f1f5f9", whiteSpace: "nowrap" },
  td: { padding: "8px 12px", borderBottom: "1px solid #f8fafc", color: "#334155", verticalAlign: "middle" },

  closeFullBtn: { padding: "8px 20px", background: "#f1f5f9", border: "none", borderRadius: 8, fontFamily: "inherit", fontSize: "0.84rem", fontWeight: 600, color: "#475569", cursor: "pointer" },
};

const ts = {
  badge: { display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 100, fontSize: "0.68rem", fontWeight: 700 },
};
